import { Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { Payable } from '../../../../models/entities/payable';
import { PayableExpenseCategory } from '../../../../models/entities/payable-expense-category';
import { PayableActionType, PaymentMethod, PaymentSchedule } from '../../../../enums/payables/payable-action.enum';
import { PayableFulfillmentType } from '../../../../models/entities/payable-fulfillment';
import moment from 'moment-timezone';
import { CommonModule } from '@angular/common';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';

@Component({
  selector: 'app-payable-review',
  templateUrl: './payable-review.component.html',
  styleUrls: ['./payable-review.component.scss'],
    standalone: true,
    imports: [CommonModule,
        LegfiDatePipe,
        LegfiCurrencyPipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PayableReviewComponent {
    @Input() payable: Payable;
    @Input() paymentMethod: string;
    @Input() whenToPay: string;
    @Input() paymentDate: any;
    @Input() invoiceAction: string;
    @Input() useContainer: boolean = true;
    @Input() narrow: boolean = false;
    @Input() displayDisclaimer: boolean = true;
    @Input() displayPaymentDetails: boolean = true;
    /** SD-9003: which entry will represent a marked-as-paid payment in the ledger; omitted when null. */
    @Input() journalEntrySummary: string | null = null;

    readonly PaymentMethod = PaymentMethod;

    get currentPaymentMethod(): string {
        return this.paymentMethod || this.payable?.currentFulfillment?.fulfillmentType;
    }

    get currentAction(): string {
        return this.invoiceAction;
    }

    get paymentMethodDescription(): string {
        // Don't show payment method for non-payment actions
        if (this.currentAction === PayableActionType.APPROVAL_ONLY) {
            return 'Send for approval only - no payment scheduled';
        }
        if (this.currentAction === PayableActionType.SAVE_DRAFT) {
            return 'Save as draft - no payment scheduled';
        }
        if (this.currentAction === PayableActionType.MARK_PAID) {
            return 'Mark as already paid';
        }

        const method = this.currentPaymentMethod;
        if (method === PaymentMethod.VENDOR_DECIDE || method === PayableFulfillmentType.FULFILLMENT_TYPE_PAYABLI_BILL_PAYMENT) {
            return 'Let the vendor decide';
        } else if (method === PaymentMethod.MAIL_CHECK || method === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT) {
            return 'Mail a check';
        } else if (method === PaymentMethod.PRINT_CHECK || method === PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK) {
            return 'Print a check';
        } else if (method === PaymentMethod.ACH_TRANSFER) {
            return 'ACH Transfer';
        }
        return '';
    }

    get paymentDateDescription(): string {
        if ([
            PayableActionType.APPROVAL_ONLY,
            PayableActionType.SAVE_DRAFT,
            PayableActionType.MARK_PAID,
        ].includes(this.currentAction as PayableActionType)
        ) {
            return '--';
        }

        if (this.whenToPay) {
            if (this.whenToPay === PaymentSchedule.IMMEDIATE) {
                return 'Process payment immediately' + (this.payable?.currentFulfillment?.approvalRule ? ' after approval' : '');
            } else if (this.whenToPay === PaymentSchedule.NO_EARLIER_THAN && this.paymentDate) {
                return `Process payment no earlier than ${moment(this.paymentDate).format('MM/DD/YYYY')}`;
            }
        } else if (this.paymentDate && this.paymentDate !== '--') {
            return moment(this.paymentDate).format('MM/DD/YYYY');
        } else if (this.payable?.scheduledPaymentDate) {
            return `Process payment no earlier than ${moment(this.payable.scheduledPaymentDate).format('MM/DD/YYYY')}`;
        }

        return '--';
    }

    get disclaimerText(): string {
        // Don't show a disclaimer for non-payment actions
        if ([
            PayableActionType.APPROVAL_ONLY,
            PayableActionType.SAVE_DRAFT,
            PayableActionType.MARK_PAID
        ].includes(this.currentAction as PayableActionType) || !this.displayDisclaimer) {
            return '';
        }

        let text = '';
        const fulfillmentType = this.currentPaymentMethod || this.payable?.currentFulfillment?.fulfillmentType;

        if (this.payable?.currentFulfillment?.approvalRule) {
            text += '* The payment process will begin no earlier than the date you select, after the invoice is approved.';
        } else {
            text += '* The payment process will begin on the date you select.';
        }

        if (fulfillmentType === PaymentMethod.VENDOR_DECIDE || fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_PAYABLI_BILL_PAYMENT) {
            text += ' Payabli will then contact the vendor to get their payment preference. If the vendor does not respond within 48 hours, a check will be mailed.';
        } else if (fulfillmentType === PaymentMethod.MAIL_CHECK || fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT) {
            text += ' Then, the check will take 2 days to print and an additional 6-10 days to arrive at the vendor.';
        }

        return text;
    }

    trackByExpenseCategoryId(index: number, category: PayableExpenseCategory): number {
        return category.expenseCategoryId;
    }
}
